// Protocol Service
// Complete CRUD operations for Protocols, Steps, and Actions

import { 
  collection, 
  doc, 
  addDoc, 
  setDoc,
  updateDoc, 
  deleteDoc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  orderBy,
  Timestamp,
  writeBatch
} from 'firebase/firestore';
import { db } from '../firebase';
import auditService from './auditService';

export class ProtocolService {
  constructor() {
    this.protocolsCollection = collection(db, 'protocols');
  }
  
  // ==================== PROTOCOL CRUD ====================
  
  /**
   * Create a new protocol
   */
  async createProtocol(protocolData, userId) {
    const now = Timestamp.now();
    
    const protocol = {
      ...protocolData,
      protocol_code: protocolData.protocol_code || '',
      evidence_base: protocolData.evidence_base || {
        guidelines: [],
        standards: [],
        regulations: []
      },
      steps: protocolData.steps || [],
      totalEstimatedDuration: 0,
      overallRiskProfile: {
        baseRPN: 0,
        maxRPN: 0,
        riskCategories: [],
        criticalSteps: 0
      },
      status: 'Draft',
      createdAt: now,
      createdBy: userId,
      updatedAt: now,
      updatedBy: userId,
      previous_version_id: null,
      change_summary: '',
      is_active: protocolData.isActive !== false
    };
    
    // Remove undefined values before saving to Firestore
    const cleanedProtocol = this.removeUndefinedValues(protocol);
    
    // Always use addDoc to generate a proper Firestore document ID
    // Don't use hardcoded IDs - let Firestore generate them
    const docRef = await addDoc(this.protocolsCollection, cleanedProtocol);
    try {
      auditService.logCreate('protocols', docRef.id, protocolData.name || protocolData.protocol_code || 'Unknown', { protocol_code: protocolData.protocol_code });
    } catch (e) {
      console.warn('Audit log failed:', e);
    }
    return docRef.id;
  }
  
  /**
   * Get protocol by ID
   */
  async getProtocol(protocolId) {
    const docRef = doc(this.protocolsCollection, protocolId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() };
    }
    return null;
  }
  
  /**
   * Duplicate protocol for versioning
   */
  async duplicateProtocol(protocolId, userId, changeSummary = '') {
    const original = await this.getProtocol(protocolId);
    if (!original) {
      throw new Error('Protocol not found');
    }
    
    const now = Timestamp.now();
    
    // Create new version
    const newVersion = {
      ...original,
      // Remove Firestore ID so it creates a new document
      firestoreId: undefined,
      id: undefined,
      
      // Versioning fields
      previous_version_id: protocolId,
      change_summary: changeSummary,
      status: 'Draft',
      
      // Update timestamps
      createdAt: now,
      createdBy: userId,
      updatedAt: now,
      updatedBy: userId,
      
      // Increment version
      version: this.incrementVersion(original.version || '1.0')
    };
    
    delete newVersion.firestoreId;
    
    const docRef = await addDoc(this.protocolsCollection, newVersion);
    
    // Create version history entry
    await this.createVersionHistory(protocolId, docRef.id, original.version || '1.0', newVersion.version, changeSummary, userId);
    
    return docRef.id;
  }
  
  /**
   * Increment version number
   */
  incrementVersion(currentVersion) {
    const parts = currentVersion.split('.');
    if (parts.length === 1) {
      return `${parseInt(parts[0]) + 1}.0`;
    }
    const major = parseInt(parts[0]);
    const minor = parseInt(parts[1]);
    return `${major}.${minor + 1}`;
  }
  
  /**
   * Create version history entry
   */
  async createVersionHistory(originalProtocolId, newProtocolId, oldVersion, newVersion, changeSummary, userId) {
    const versionsCollection = collection(db, 'protocol_versions');
    const now = Timestamp.now();
    
    await addDoc(versionsCollection, {
      original_protocol_id: originalProtocolId,
      new_protocol_id: newProtocolId,
      old_version: oldVersion,
      new_version: newVersion,
      change_summary: changeSummary,
      created_at: now,
      created_by: userId
    });
  }
  
  /**
   * Get version history for a protocol
   */
  async getVersionHistory(protocolId) {
    const versionsCollection = collection(db, 'protocol_versions');
    const q = query(
      versionsCollection,
      where('original_protocol_id', '==', protocolId),
      orderBy('created_at', 'desc')
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  }
  
  /**
   * Rollback to previous version
   */
  async rollbackToVersion(protocolId, versionId, userId, changeSummary = 'Rollback to previous version') {
    const versionHistory = await this.getVersionHistory(protocolId);
    const targetVersion = versionHistory.find(v => v.new_protocol_id === versionId);
    
    if (!targetVersion) {
      throw new Error('Version not found');
    }
    
    const oldProtocol = await this.getProtocol(versionId);
    if (!oldProtocol) {
      throw new Error('Previous version protocol not found');
    }
    
    // Create new version from old one
    return await this.duplicateProtocol(versionId, userId, changeSummary);
  }
  
  /**
   * Get all protocols with optional filters
   */
  async getAllProtocols(filters) {
    let q = query(this.protocolsCollection);
    
    // Apply filters one at a time to avoid composite index issues
    if (filters?.category) {
      q = query(q, where('category', '==', filters.category));
    }
    if (filters?.status) {
      q = query(q, where('status', '==', filters.status));
    }
    
    // For now, get all protocols and filter in memory to avoid index issues
    // TODO: Create proper composite indexes in Firebase Console
    
    const snapshot = await getDocs(q);
    let protocols = snapshot.docs.map(doc => ({
      firestoreId: doc.id, // Use firestoreId for the document ID
      ...doc.data()
    }));
    
    // Apply additional filters in memory to avoid index issues
    if (filters?.includeInactive !== true) {
      protocols = protocols.filter(p => p.isActive !== false);
    }
    if (filters?.isCritical !== undefined) {
      protocols = protocols.filter(p => p.isCritical === filters.isCritical);
    }
    if (filters?.applicableLocation) {
      protocols = protocols.filter(p => 
        p.applicableLocations && p.applicableLocations.includes(filters.applicableLocation)
      );
    }
    if (filters?.departmentId) {
      protocols = protocols.filter(p => 
        p.applicableDepartments && p.applicableDepartments.includes(filters.departmentId)
      );
    }
    if (filters?.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase();
      protocols = protocols.filter(p => 
        p.name.toLowerCase().includes(searchLower) ||
        (p.shortName && p.shortName.toLowerCase().includes(searchLower)) ||
        p.description.toLowerCase().includes(searchLower)
      );
    }
    
    // Sort by name
    protocols.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    
        // No need for client-side deduplication - let the database handle it
        return protocols;
  }
  
  /**
   * Get protocols by category
   */
  async getProtocolsByCategory(category) {
    const q = query(
      this.protocolsCollection,
      where('category', '==', category),
      orderBy('name', 'asc')
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  }
  
  /**
   * Get active protocols only
   */
  async getActiveProtocols() {
    const q = query(
      this.protocolsCollection,
      where('is_active', '==', true),
      where('status', '==', 'Active'),
      orderBy('name', 'asc')
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  }

  /**
   * Get protocols by department
   */
  async getProtocolsByDepartment(departmentId) {
    const q = query(
      this.protocolsCollection,
      where('applicableDepartments', 'array-contains', departmentId),
      where('is_active', '==', true),
      orderBy('name', 'asc')
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  }

  /**
   * Archive protocol (soft delete)
   */
  async archiveProtocol(protocolId, userId) {
    await this.updateProtocol(protocolId, {
      is_active: false,
      status: 'Archived',
      archivedAt: Timestamp.now(),
      archivedBy: userId
    }, userId);
  }
  
  /**
   * Remove undefined values from an object recursively
   * Firestore doesn't accept undefined, but accepts null and empty arrays
   */
  removeUndefinedValues(obj) {
    if (obj === undefined) {
      return undefined; // Will be filtered out at object level
    }
    
    if (obj === null) {
      return null; // null is valid in Firestore
    }
    
    if (Array.isArray(obj)) {
      // Keep empty arrays, but remove undefined items
      const cleaned = obj.map(item => this.removeUndefinedValues(item))
        .filter(item => item !== undefined);
      return cleaned;
    }
    
    if (typeof obj === 'object' && obj.constructor === Object) {
      const cleaned = {};
      for (const [key, value] of Object.entries(obj)) {
        if (value !== undefined) {
          const cleanedValue = this.removeUndefinedValues(value);
          // Only add if cleaned value is not undefined
          if (cleanedValue !== undefined) {
            cleaned[key] = cleanedValue;
          }
        }
      }
      return cleaned;
    }
    
    return obj;
  }

  /**
   * Update protocol
   */
  async updateProtocol(protocolId, updates, userId) {
    const docRef = doc(this.protocolsCollection, protocolId);
    const current = await this.getProtocol(protocolId);
    
    // Remove undefined values before saving to Firestore
    const cleanedUpdates = this.removeUndefinedValues({
      ...updates,
      updatedAt: Timestamp.now(),
      updatedBy: userId
    });
    
    await updateDoc(docRef, cleanedUpdates);
    try {
      auditService.logUpdate('protocols', protocolId, current?.name || current?.protocol_code || protocolId, { changedFields: Object.keys(updates) });
    } catch (e) {
      console.warn('Audit log failed:', e);
    }
  }
  
  /**
   * Delete protocol (soft delete by setting status to Deprecated)
   */
  async deleteProtocol(protocolId, userId) {
    const current = await this.getProtocol(protocolId);
    await this.updateProtocol(protocolId, {
      status: 'Deprecated',
      expiryDate: Timestamp.now()
    }, userId);
    try {
      auditService.logDelete('protocols', protocolId, current?.name || current?.protocol_code || protocolId, { status: 'Deprecated' });
    } catch (e) {
      console.warn('Audit log failed:', e);
    }
  }
  
  /**
   * Hard delete protocol (permanent removal)
   */
  async hardDeleteProtocol(protocolId) {
    const docRef = doc(this.protocolsCollection, protocolId);
    await deleteDoc(docRef);
  }


  /**
   * Clean up duplicate protocols
   */
  async cleanupDuplicateProtocols() {
    console.log('🧹 Starting duplicate protocol cleanup...');
    
    try {
      // Get ALL protocols directly from Firestore (bypass deduplication)
      const q = query(this.protocolsCollection);
      const snapshot = await getDocs(q);
      const allProtocols = snapshot.docs.map(doc => ({
        firestoreId: doc.id, // Firestore document ID
        ...doc.data()
      }));
      
      console.log(`Found ${allProtocols.length} total protocols in database`);
      
      // Group by protocol name (since we're not using hardcoded IDs anymore)
      const nameGroups = {};
      allProtocols.forEach(protocol => {
        const name = protocol.name;
        if (name) {
          if (!nameGroups[name]) {
            nameGroups[name] = [];
          }
          nameGroups[name].push(protocol);
        }
      });
      
      console.log(`Found ${Object.keys(nameGroups).length} unique protocol names`);
      
      // Find and remove duplicates by name
      let duplicatesRemoved = 0;
      for (const [name, protocols] of Object.entries(nameGroups)) {
        if (protocols.length > 1) {
          console.log(`🔍 Found ${protocols.length} protocols with duplicate name "${name}"`);
          console.log(`   Protocols:`, protocols.map(p => ({ 
            firestoreId: p.firestoreId, 
            name: p.name, 
            createdAt: p.createdAt,
            status: p.status,
            isActive: p.isActive
          })));
          
          // Sort by creation date (keep oldest)
          const sortedProtocols = protocols.sort((a, b) => {
            const dateA = new Date(a.createdAt?.toDate?.() || a.createdAt || 0);
            const dateB = new Date(b.createdAt?.toDate?.() || b.createdAt || 0);
            return dateA - dateB;
          });
          
          // Keep the first (oldest) one
          const keepProtocol = sortedProtocols[0];
          console.log(`✅ Keeping protocol: ${keepProtocol.firestoreId} (${keepProtocol.name}) - Created: ${keepProtocol.createdAt}`);
          
          // Remove all others
          const duplicates = sortedProtocols.slice(1);
          for (const duplicate of duplicates) {
            try {
              console.log(`🗑️ Removing duplicate: ${duplicate.firestoreId} (${duplicate.name}) - Created: ${duplicate.createdAt}`);
              // Use the Firestore document ID for deletion (not the data ID)
              await this.deleteProtocol(duplicate.firestoreId, 'system-cleanup');
              duplicatesRemoved++;
              console.log(`✅ Successfully removed duplicate: ${duplicate.firestoreId}`);
            } catch (error) {
              console.warn(`⚠️ Failed to remove duplicate ${duplicate.firestoreId}:`, error.message);
              // Try hard delete as fallback
              try {
                console.log(`🔄 Trying hard delete for ${duplicate.firestoreId}`);
                await this.hardDeleteProtocol(duplicate.firestoreId);
                duplicatesRemoved++;
                console.log(`✅ Successfully hard deleted duplicate: ${duplicate.firestoreId}`);
              } catch (hardError) {
                console.error(`❌ Failed to hard delete ${duplicate.firestoreId}:`, hardError.message);
              }
            }
          }
        }
      }
      
      console.log(`✅ Cleanup complete. Removed ${duplicatesRemoved} duplicate protocols.`);
      return duplicatesRemoved;
    } catch (error) {
      console.error('❌ Error during cleanup:', error);
      throw error;
    }
  }
  
  /**
   * Create a copy of a protocol with a new version string (simple copy, no version history).
   * For versioning/rollback use duplicateProtocol(protocolId, userId, changeSummary).
   */
  async duplicateProtocolAsCopy(protocolId, newVersion, userId) {
    const original = await this.getProtocol(protocolId);
    if (!original) throw new Error('Protocol not found');
    
    const duplicate = {
      ...original,
      name: `${original.name} (Copy)`,
      version: newVersion,
      status: 'Draft'
    };
    
    return await this.createProtocol(duplicate, userId);
  }
  
  /**
   * Submit protocol for review (draft → review)
   * Validates protocol before submission
   */
  async submitForReview(protocolId, userId, reviewNotes = '') {
    const protocol = await this.getProtocol(protocolId);
    if (!protocol) {
      throw new Error('Protocol not found');
    }
    
    // Validate protocol can be submitted
    if (protocol.status !== 'Draft' && protocol.status !== 'draft') {
      throw new Error(`Protocol cannot be submitted for review. Current status: ${protocol.status}`);
    }
    
    // Validate protocol has required fields
    const validation = this.validateProtocol(protocol);
    if (!validation.valid) {
      throw new Error(`Protocol validation failed: ${validation.errors.join(', ')}`);
    }
    
    const now = Timestamp.now();
    
    await this.updateProtocol(protocolId, {
      status: 'Under Review',
      submittedForReviewAt: now,
      submittedForReviewBy: userId,
      reviewNotes: reviewNotes,
      previousStatus: protocol.status
    }, userId);
    
    // Log action for audit trail
    await this.logProtocolAction(protocolId, userId, 'SUBMIT_FOR_REVIEW', {
      reviewNotes: reviewNotes,
      previousStatus: protocol.status
    });
  }
  
  /**
   * Approve protocol (review → approved/active)
   * Can only approve protocols that are under review
   */
  async approveProtocol(protocolId, approverUserId, approvalNotes = '') {
    const protocol = await this.getProtocol(protocolId);
    if (!protocol) {
      throw new Error('Protocol not found');
    }
    
    // Validate protocol can be approved
    const validStatuses = ['Under Review', 'under review', 'Review', 'review'];
    if (!validStatuses.includes(protocol.status)) {
      throw new Error(`Protocol cannot be approved. Current status: ${protocol.status}`);
    }
    
    const now = Timestamp.now();
    
    await this.updateProtocol(protocolId, {
      status: 'Active',
      approvedBy: approverUserId,
      approvedAt: now,
      effectiveDate: now,
      approvalNotes: approvalNotes,
      previousStatus: protocol.status
    }, approverUserId);
    
    // Log action for audit trail
    await this.logProtocolAction(protocolId, approverUserId, 'APPROVE_PROTOCOL', {
      approvalNotes: approvalNotes,
      previousStatus: protocol.status
    });
  }
  
  /**
   * Reject protocol (review → draft)
   * Returns protocol to draft status with rejection reason
   */
  async rejectProtocol(protocolId, rejectorUserId, rejectionReason = '') {
    const protocol = await this.getProtocol(protocolId);
    if (!protocol) {
      throw new Error('Protocol not found');
    }
    
    const validStatuses = ['Under Review', 'under review', 'Review', 'review'];
    if (!validStatuses.includes(protocol.status)) {
      throw new Error(`Protocol cannot be rejected. Current status: ${protocol.status}`);
    }
    
    const now = Timestamp.now();
    
    await this.updateProtocol(protocolId, {
      status: 'Draft',
      rejectedAt: now,
      rejectedBy: rejectorUserId,
      rejectionReason: rejectionReason,
      previousStatus: protocol.status
    }, rejectorUserId);
    
    // Log action for audit trail
    await this.logProtocolAction(protocolId, rejectorUserId, 'REJECT_PROTOCOL', {
      rejectionReason: rejectionReason,
      previousStatus: protocol.status
    });
  }
  
  /**
   * Log protocol action for audit trail
   * Internal method to log protocol state changes
   */
  async logProtocolAction(protocolId, userId, actionType, metadata = {}) {
    try {
      // Note: This assumes you have an audit_logs collection
      // If not, this could be implemented as a subcollection or separate service
      const auditLogsCollection = collection(db, 'protocol_audit_logs');
      const now = Timestamp.now();
      
      await addDoc(auditLogsCollection, {
        protocolId: protocolId,
        userId: userId,
        actionType: actionType,
        timestamp: now,
        metadata: metadata
      });
    } catch (error) {
      // Don't fail the main operation if audit logging fails
      console.error('Error logging protocol action:', error);
    }
  }
  
  /**
   * Deprecate protocol
   */
  async deprecateProtocol(protocolId, userId) {
    await this.updateProtocol(protocolId, {
      status: 'Deprecated',
      expiryDate: Timestamp.now()
    }, userId);
  }
  
  // ==================== STEP MANAGEMENT ====================
  
  /**
   * Add step to protocol
   */
  async addStep(protocolId, stepData, userId) {
    const protocol = await this.getProtocol(protocolId);
    if (!protocol) throw new Error('Protocol not found');
    
    const now = Timestamp.now();
    const newStep = {
      ...stepData,
      id: this.generateId('step'),
      protocolId,
      sequenceNumber: protocol.steps.length + 1,
      actions: [],
      createdAt: now,
      updatedAt: now
    };
    
    const updatedSteps = [...protocol.steps, newStep];
    const totalDuration = this.calculateTotalDuration(updatedSteps);
    const criticalSteps = updatedSteps.filter(s => s.isCritical).length;
    
    await this.updateProtocol(protocolId, {
      steps: updatedSteps,
      totalEstimatedDuration: totalDuration,
      overallRiskProfile: {
        ...protocol.overallRiskProfile,
        criticalSteps
      }
    }, userId);
  }
  
  /**
   * Update step
   */
  async updateStep(protocolId, stepId, updates, userId) {
    const protocol = await this.getProtocol(protocolId);
    if (!protocol) throw new Error('Protocol not found');
    
    const now = Timestamp.now();
    const updatedSteps = protocol.steps.map(step =>
      step.id === stepId
        ? { ...step, ...updates, updatedAt: now }
        : step
    );
    
    const totalDuration = this.calculateTotalDuration(updatedSteps);
    const criticalSteps = updatedSteps.filter(s => s.isCritical).length;
    
    await this.updateProtocol(protocolId, {
      steps: updatedSteps,
      totalEstimatedDuration: totalDuration,
      overallRiskProfile: {
        ...protocol.overallRiskProfile,
        criticalSteps
      }
    }, userId);
  }
  
  /**
   * Delete step
   */
  async deleteStep(protocolId, stepId, userId) {
    const protocol = await this.getProtocol(protocolId);
    if (!protocol) throw new Error('Protocol not found');
    
    // Remove step and resequence
    const updatedSteps = protocol.steps
      .filter(step => step.id !== stepId)
      .map((step, index) => ({ ...step, sequenceNumber: index + 1 }));
    
    const totalDuration = this.calculateTotalDuration(updatedSteps);
    const criticalSteps = updatedSteps.filter(s => s.isCritical).length;
    
    await this.updateProtocol(protocolId, {
      steps: updatedSteps,
      totalEstimatedDuration: totalDuration,
      overallRiskProfile: {
        ...protocol.overallRiskProfile,
        criticalSteps
      }
    }, userId);
  }
  
  /**
   * Reorder steps
   */
  async reorderSteps(protocolId, stepIds, userId) {
    const protocol = await this.getProtocol(protocolId);
    if (!protocol) throw new Error('Protocol not found');
    
    // Resequence based on new order
    const reorderedSteps = stepIds.map((stepId, index) => {
      const step = protocol.steps.find(s => s.id === stepId);
      if (!step) throw new Error(`Step ${stepId} not found`);
      return { ...step, sequenceNumber: index + 1 };
    });
    
    await this.updateProtocol(protocolId, {
      steps: reorderedSteps
    }, userId);
  }
  
  // ==================== ACTION MANAGEMENT ====================
  
  /**
   * Add action to step
   */
  async addAction(protocolId, stepId, actionData, userId) {
    const protocol = await this.getProtocol(protocolId);
    if (!protocol) throw new Error('Protocol not found');
    
    const now = Timestamp.now();
    const updatedSteps = protocol.steps.map(step => {
      if (step.id === stepId) {
        const newAction = {
          ...actionData,
          id: this.generateId('action'),
          stepId,
          sequenceNumber: step.actions.length + 1,
          createdAt: now,
          updatedAt: now
        };
        
        return {
          ...step,
          actions: [...step.actions, newAction]
        };
      }
      return step;
    });
    
    await this.updateProtocol(protocolId, {
      steps: updatedSteps
    }, userId);
  }
  
  /**
   * Update action
   */
  async updateAction(protocolId, stepId, actionId, updates, userId) {
    const protocol = await this.getProtocol(protocolId);
    if (!protocol) throw new Error('Protocol not found');
    
    const now = Timestamp.now();
    const updatedSteps = protocol.steps.map(step => {
      if (step.id === stepId) {
        return {
          ...step,
          actions: step.actions.map(action =>
            action.id === actionId
              ? { ...action, ...updates, updatedAt: now }
              : action
          )
        };
      }
      return step;
    });
    
    await this.updateProtocol(protocolId, {
      steps: updatedSteps
    }, userId);
  }
  
  /**
   * Delete action
   */
  async deleteAction(protocolId, stepId, actionId, userId) {
    const protocol = await this.getProtocol(protocolId);
    if (!protocol) throw new Error('Protocol not found');
    
    const updatedSteps = protocol.steps.map(step => {
      if (step.id === stepId) {
        return {
          ...step,
          actions: step.actions
            .filter(action => action.id !== actionId)
            .map((action, index) => ({ ...action, sequenceNumber: index + 1 }))
        };
      }
      return step;
    });
    
    await this.updateProtocol(protocolId, {
      steps: updatedSteps
    }, userId);
  }
  
  /**
   * Reorder actions within a step
   */
  async reorderActions(protocolId, stepId, actionIds, userId) {
    const protocol = await this.getProtocol(protocolId);
    if (!protocol) throw new Error('Protocol not found');
    
    const updatedSteps = protocol.steps.map(step => {
      if (step.id === stepId) {
        const reorderedActions = actionIds.map((actionId, index) => {
          const action = step.actions.find(a => a.id === actionId);
          if (!action) throw new Error(`Action ${actionId} not found`);
          return { ...action, sequenceNumber: index + 1 };
        });
        
        return {
          ...step,
          actions: reorderedActions
        };
      }
      return step;
    });
    
    await this.updateProtocol(protocolId, {
      steps: updatedSteps
    }, userId);
  }
  
  // ==================== HELPER METHODS ====================
  
  /**
   * Calculate total protocol duration from all steps
   */
  calculateTotalDuration(steps) {
    return steps.reduce((total, step) => {
      const stepDuration = step.durationUnit === 'minutes' 
        ? step.estimatedDuration 
        : step.estimatedDuration / 60;
      return total + stepDuration;
    }, 0);
  }
  
  /**
   * Generate unique ID for steps and actions
   */
  generateId(prefix) {
    return `${prefix}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }
  
  /**
   * Validate protocol before approval
   */
  validateProtocol(protocol) {
    const errors = [];
    
    // Check required fields
    if (!protocol.name) errors.push('Protocol name is required');
    if (!protocol.shortName) errors.push('Protocol short name is required');
    if (!protocol.category) errors.push('Protocol category is required');
    if (protocol.steps.length === 0) errors.push('Protocol must have at least one step');
    
    // Check steps
    protocol.steps.forEach((step, index) => {
      if (!step.name) errors.push(`Step ${index + 1} name is required`);
      if (step.estimatedDuration <= 0) errors.push(`Step ${index + 1} duration must be greater than 0`);
      
      // Check actions
      step.actions.forEach((action, actionIndex) => {
        if (!action.name) errors.push(`Step ${index + 1}, Action ${actionIndex + 1} name is required`);
        if (action.estimatedDuration <= 0) errors.push(`Step ${index + 1}, Action ${actionIndex + 1} duration must be greater than 0`);
      });
    });
    
    // Check applicability
    if (protocol.applicableLocations.length === 0) {
      errors.push('Protocol must be applicable to at least one location type');
    }
    
    return {
      valid: errors.length === 0,
      errors
    };
  }
  
  // ==================== PROTOCOL COMPLETION TRACKING ====================
  
  /**
   * Get protocol completion statistics
   * Aggregates data from all completed tasks that used this protocol
   */
  async getProtocolCompletionStats(protocolId, dateRange = null) {
    try {
      // Import taskService dynamically to avoid circular dependency
      const { taskService } = await import('./taskService');
      
      // Get all tasks that contain this protocol
      const allTasks = await taskService.getAllTasks({});
      
      // Filter tasks and extract protocol execution data
      const protocolExecutions = [];
      
      for (const task of allTasks) {
        if (!task.protocols || !Array.isArray(task.protocols)) continue;
        
        for (const protocolExecution of task.protocols) {
          // Match by protocolId
          if (protocolExecution.protocolId === protocolId || 
              protocolExecution.protocolId === this.getProtocolFirestoreId(protocolId)) {
            
            // Apply date range filter if provided
            if (dateRange) {
              const executionDate = protocolExecution.endTime || task.actualEnd || task.createdAt;
              if (executionDate) {
                const execDate = executionDate.toDate ? executionDate.toDate() : new Date(executionDate);
                const startDate = new Date(dateRange.start);
                const endDate = new Date(dateRange.end);
                
                if (execDate < startDate || execDate > endDate) {
                  continue;
                }
              }
            }
            
            protocolExecutions.push({
              taskId: task.id,
              taskName: task.name,
              protocolStatus: protocolExecution.status,
              completionDate: protocolExecution.endTime || task.actualEnd,
              duration: protocolExecution.duration,
              complianceScore: protocolExecution.complianceScore,
              completedSteps: (protocolExecution.steps || []).filter(s => s.isComplete).length,
              skippedSteps: (protocolExecution.steps || []).filter(s => s.isSkipped).length,
              totalSteps: (protocolExecution.steps || []).length,
              onTimeSteps: (protocolExecution.steps || []).filter(s => s.isComplete && s.isOnTime).length
            });
          }
        }
      }
      
      // Calculate statistics — both 'completed' and 'completed_with_skips' count as finalized
      const completed = protocolExecutions.filter(
        e => e.protocolStatus === 'completed' || e.protocolStatus === 'completed_with_skips'
      );
      const total = protocolExecutions.length;
      const completionRate = total > 0 ? (completed.length / total) * 100 : 0;
      
      const avgComplianceScore = completed.length > 0
        ? completed.reduce((sum, e) => sum + (e.complianceScore || 0), 0) / completed.length
        : 0;
      
      const avgDuration = completed.length > 0
        ? completed.reduce((sum, e) => sum + (e.duration || 0), 0) / completed.length
        : 0;
      
      const totalCompletedSteps = completed.reduce((sum, e) => sum + e.completedSteps, 0);
      const totalSkippedSteps = completed.reduce((sum, e) => sum + e.skippedSteps, 0);
      const totalSteps = completed.reduce((sum, e) => sum + e.totalSteps, 0);
      const totalOnTimeSteps = completed.reduce((sum, e) => sum + e.onTimeSteps, 0);
      
      const onTimeRate = totalSteps > 0 ? (totalOnTimeSteps / totalSteps) * 100 : 0;
      
      return {
        protocolId,
        totalExecutions: total,
        completedExecutions: completed.length,
        completionRate: Math.round(completionRate * 100) / 100,
        averageComplianceScore: Math.round(avgComplianceScore * 100) / 100,
        averageDuration: Math.round(avgDuration),
        totalCompletedSteps,
        totalSkippedSteps,
        totalSteps,
        onTimeRate: Math.round(onTimeRate * 100) / 100,
        executions: protocolExecutions
      };
    } catch (error) {
      console.error('Error getting protocol completion stats:', error);
      throw error;
    }
  }
  
  /**
   * Get completion statistics for all protocols
   */
  async getAllProtocolsCompletionStats(dateRange = null) {
    try {
      const allProtocols = await this.getAllProtocols({});
      const stats = [];
      
      for (const protocol of allProtocols) {
        const protocolStats = await this.getProtocolCompletionStats(
          protocol.id || protocol.firestoreId,
          dateRange
        );
        stats.push({
          ...protocolStats,
          protocolName: protocol.name,
          protocolCode: protocol.protocol_code,
          protocolVersion: protocol.version
        });
      }
      
      return stats.sort((a, b) => b.totalExecutions - a.totalExecutions);
    } catch (error) {
      console.error('Error getting all protocols completion stats:', error);
      throw error;
    }
  }
  
  /**
   * Get protocol completion trends over time
   */
  async getProtocolCompletionTrend(protocolId, days = 30) {
    try {
      const { taskService } = await import('./taskService');
      const allTasks = await taskService.getAllTasks({});
      
      const dailyStats = {};
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);
      
      // Initialize daily stats
      for (let i = 0; i < days; i++) {
        const date = new Date(startDate);
        date.setDate(date.getDate() + i);
        const dateKey = date.toISOString().split('T')[0];
        dailyStats[dateKey] = {
          date: dateKey,
          completed: 0,
          total: 0,
          avgComplianceScore: 0,
          avgDuration: 0
        };
      }
      
      // Aggregate protocol executions by date
      for (const task of allTasks) {
        if (!task.protocols || !Array.isArray(task.protocols)) continue;
        
        for (const protocolExecution of task.protocols) {
          if (protocolExecution.protocolId === protocolId ||
              protocolExecution.protocolId === this.getProtocolFirestoreId(protocolId)) {
            
            const execDate = protocolExecution.endTime || task.actualEnd || task.createdAt;
            if (execDate) {
              const date = execDate.toDate ? execDate.toDate() : new Date(execDate);
              const dateKey = date.toISOString().split('T')[0];
              
              if (dailyStats[dateKey]) {
                dailyStats[dateKey].total++;
                if (protocolExecution.status === 'completed') {
                  dailyStats[dateKey].completed++;
                  dailyStats[dateKey].avgComplianceScore += protocolExecution.complianceScore || 0;
                  dailyStats[dateKey].avgDuration += protocolExecution.duration || 0;
                }
              }
            }
          }
        }
      }
      
      // Calculate averages
      Object.values(dailyStats).forEach(stat => {
        if (stat.completed > 0) {
          stat.avgComplianceScore = Math.round((stat.avgComplianceScore / stat.completed) * 100) / 100;
          stat.avgDuration = Math.round(stat.avgDuration / stat.completed);
        }
        stat.completionRate = stat.total > 0 ? (stat.completed / stat.total) * 100 : 0;
      });
      
      return Object.values(dailyStats).sort((a, b) => a.date.localeCompare(b.date));
    } catch (error) {
      console.error('Error getting protocol completion trend:', error);
      throw error;
    }
  }
  
  /**
   * Helper to get Firestore ID from protocol ID
   */
  getProtocolFirestoreId(protocolId) {
    // If protocolId is already a Firestore ID, return it
    // Otherwise, try to match by id or firestoreId field
    return protocolId;
  }

  // ==================== TASK PACKAGE MANAGEMENT ====================
  
  /**
   * Add task package to protocol
   */
  async addTaskPackage(protocolId, taskPackageData, userId) {
    try {
      await this.updateProtocol(protocolId, {
        taskPackage: {
          enabled: true,
          generateMultipleTasks: true,
          tasks: taskPackageData.tasks || [],
          packageSettings: taskPackageData.packageSettings || {
            allTasksMustComplete: true,
            minimumComplianceScore: 90,
            escalateIfDelayed: true,
            escalationThreshold: 30,
            notifyOnCompletion: true,
            notificationGroups: []
          }
        }
      }, userId);
      
      console.log(`✅ Added task package to protocol: ${protocolId}`);
    } catch (error) {
      console.error('Error adding task package:', error);
      throw error;
    }
  }
  
  /**
   * Update task package
   */
  async updateTaskPackage(protocolId, taskPackageUpdates, userId) {
    try {
      const protocol = await this.getProtocol(protocolId);
      if (!protocol) throw new Error('Protocol not found');
      
      const updatedPackage = {
        ...protocol.taskPackage,
        ...taskPackageUpdates
      };
      
      await this.updateProtocol(protocolId, {
        taskPackage: updatedPackage
      }, userId);
      
      console.log(`✅ Updated task package for protocol: ${protocolId}`);
    } catch (error) {
      console.error('Error updating task package:', error);
      throw error;
    }
  }
  
  /**
   * Remove task package from protocol
   */
  async removeTaskPackage(protocolId, userId) {
    try {
      await this.updateProtocol(protocolId, {
        taskPackage: {
          enabled: false,
          generateMultipleTasks: false,
          tasks: [],
          packageSettings: {}
        }
      }, userId);
      
      console.log(`✅ Removed task package from protocol: ${protocolId}`);
    } catch (error) {
      console.error('Error removing task package:', error);
      throw error;
    }
  }
  
  /**
   * Get protocol task package
   */
  async getTaskPackage(protocolId) {
    try {
      const protocol = await this.getProtocol(protocolId);
      if (!protocol) throw new Error('Protocol not found');
      
      return protocol.taskPackage || null;
    } catch (error) {
      console.error('Error fetching task package:', error);
      throw error;
    }
  }
}

export const protocolService = new ProtocolService();

